import React, { useMemo, useState } from "react";
import {
  GoogleMap,
  Marker,
  Polyline,
  InfoWindow,
  OverlayView,
  useLoadScript,
  Circle,
} from "@react-google-maps/api";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function milesBetween(a, b) {
  const R = 3958.8; // miles
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;

  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(dLng / 2) ** 2;

  return Math.round(R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)));
}

function nauticalMilesBetween(a, b) {
  return Math.round(milesBetween(a, b) * 0.868976);
}

const storageCovers = {
  grenada: { center: { lat: 12.12, lng: -61.67 }, radius: 19000 },
  trinidad: { center: { lat: 10.43, lng: -61.45 }, radius: 71000 },
};

const language = navigator.language.toLowerCase();


const hubs = [
  { slug: "dominicanrepublic", name: "Dominican Republic", lat: 19.7808, lng: -70.6871, type: "berthing" },
  { slug: "sanjuan", name: "San Juan", lat: 18.4655, lng: -66.1057, type: "berthing" },
  { slug: "stthomas", name: "St. Thomas (USVI)", lat: 18.3381, lng: -64.9407, type: "berthing" },
  { slug: "stcroix", name: "St. Croix", lat: 17.7275, lng: -64.7481, type: "berthing" },
  { slug: "tortola", name: "British Virgin Islands (Tortola)", lat: 18.4207, lng: -64.6399, type: "berthing" },
  { slug: "virgingorda", name: "Virgin Gorda", lat: 18.4904, lng: -64.3975, type: "berthing" },
  { slug: "anguilla", name: "Anguilla", lat: 18.2206, lng: -63.0686, type: "berthing" },
  // { slug: "stmaarten", name: "St. Martin / St. Maarten", lat: 18.0708, lng: -63.0501, type: "berthing" },
  {slug: "stmaarten",  name_en: "Saint Maarten",name_fr: "Saint Martin",lat: 18.0708,lng: -63.0501,type: "berthing"},

  { slug: "stbarts", name: "St. Barthélemy", lat: 17.9, lng: -62.833, type: "berthing" },
  { slug: "saba", name: "Saba", lat: 17.635, lng: -63.233, type: "berthing" },
  { slug: "steustatius", name: "St. Eustatius", lat: 17.489, lng: -62.973, type: "berthing" },
  { slug: "stkitts", name: "St. Kitts", lat: 17.3578, lng: -62.783, type: "berthing" },
  { slug: "nevis", name: "Nevis", lat: 17.155, lng: -62.583, type: "berthing" },
  { slug: "antigua", name: "Antigua", lat: 17.127, lng: -61.846, type: "berthing" },
  { slug: "barbuda", name: "Barbuda", lat: 17.643, lng: -61.828, type: "berthing" },
  { slug: "montserrat", name: "Montserrat", lat: 16.742, lng: -62.193, type: "berthing" },
  { slug: "guadeloupe", name: "Guadeloupe", lat: 16.237, lng: -61.533, type: "berthing" },
  { slug: "lessaintes", name: "Les Saintes", lat: 15.86, lng: -61.58, type: "berthing" },
  { slug: "dominica", name: "Dominica", lat: 15.301, lng: -61.388, type: "berthing" },
  { slug: "martinique", name: "Martinique", lat: 14.616, lng: -61.058, type: "berthing" },
  { slug: "stlucia", name: "St. Lucia", lat: 14.01, lng: -60.991, type: "berthing" },
  { slug: "stvincent", name: "St. Vincent", lat: 13.16, lng: -61.224, type: "berthing" },
  { slug: "grenadines", name: "Grenadines", lat: 12.85, lng: -61.25, type: "berthing" },
  { slug: "grenada", name: "Grenada", lat: 12.056, lng: -61.748, type: "storage" },
  { slug: "trinidad", name: "Trinidad", lat: 10.667, lng: -61.518, type: "storage" },
  { slug: "tobago", name: "Tobago", lat: 11.182, lng: -60.735, type: "berthing" },
  { slug: "losroques", name: "Los Roques", lat: 11.936, lng: -66.665, type: "berthing" },
  { slug: "margarita", name: "Margarita", lat: 10.998, lng: -63.823, type: "berthing" },
  { slug: "lostestigos", name: "Los Testigos", lat: 11.385, lng: -63.126, type: "berthing" },
  { slug: "lasaves", name: "Las Aves Island", lat: 15.4018, lng: -63.3659, type: "berthing" },
];

/* ---------------- ROUTES  ---------------- */
const routes = [
  ["dominicanrepublic", "sanjuan"],
  ["sanjuan", "stthomas"],
  ["stthomas", "tortola"],
  ["tortola", "virgingorda"],
  ["stthomas", "stcroix"],
  ["stthomas", "losroques"],
  ["losroques", "martinique"],
  ["losroques", "margarita"],
  ["virgingorda", "anguilla"],
  ["stcroix", "anguilla"],
  ["anguilla", "stmaarten"],
  ["stmaarten", "stbarts"],
  ["stbarts", "saba"],
  ["stcroix", "guadeloupe"],
  ["stcroix", "martinique"],
  ["stcroix", "lasaves"],
  ["guadeloupe", "lasaves"],
  ["martinique", "lasaves"],
  ["antigua", "barbuda"],
  ["antigua", "nevis"],
  ["stbarts", "stkitts"],
  ["antigua", "guadeloupe"],
  ["antigua", "stbarts"],
  ["montserrat", "guadeloupe"],
  ["margarita", "lostestigos"],
  ["martinique", "lostestigos"],
  ["grenada", "lostestigos"],
  ["dominica", "lessaintes"],
  ["dominica", "martinique"],
  ["stlucia", "martinique"],
  ["stlucia", "stvincent"],
  ["grenada", "stvincent"],
  ["grenada", "trinidad"],
  ["tobago", "trinidad"],
];

const containerStyle = { width: "100%", height: "75vh" };
const routeStyle = { strokeColor: "#1e67ff", strokeOpacity: 0.9, strokeWeight: 2 };
const dashedRed = {
  strokeColor: "#d64545",
  strokeOpacity: 1,
  strokeWeight: 2,
  icons: [{ icon: { path: "M 0,-1 0,1", scale: 3 }, offset: "0", repeat: "12px" }],
};

export default function HubMap() {
  const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey: API_KEY });

  const [showRoutes, setShowRoutes] = useState(true);
  const [showNauticalMiles, setShowNauticalMiles] = useState(true);
  const [showBerthing, setShowBerthing] = useState(true);
  const [showStorage, setShowStorage] = useState(true);

 
  const [active, setActive] = useState(null);

  // MAP CENTER ON ANTIGUA
  // const center = useMemo(() => ({ lat: 15, lng: -61.5 }), []);
  const center = {
    lat: 17.127,
    lng: -61.846,
  }
  const hubMap = useMemo(
    () => hubs.reduce((m, h) => ((m[h.slug] = h), m), {}),
    []
  );

  const hurricaneBelt = useMemo(() => {
    const pts = [];
    for (let lng = -180; lng <= 180; lng += 1) pts.push({ lat: 12.5, lng });
    return pts;
  }, []);

  if (loadError) return <div style={{ padding: 20 }}>Map failed to load.</div>;
  if (!isLoaded) return <div style={{ padding: 20 }}>Loading map…</div>;

  const LL = (slug) => ({ lat: hubMap[slug].lat, lng: hubMap[slug].lng });

  return (
    <div style={{ position: "relative" }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={9} //map zoom
        options={{
          mapTypeId: "terrain",
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
        }}
      >
        {/* ROUTES  */}
        {showRoutes &&
          routes.map(([a, b], i) => {
            if (!hubMap[a] || !hubMap[b]) return null;
            const midpoint = {
              lat: (hubMap[a].lat + hubMap[b].lat) / 2,
              lng: (hubMap[a].lng + hubMap[b].lng) / 2,
            };

            return (
              <React.Fragment key={`${a}-${b}-${i}`}>
              <Polyline
                path={[LL(a), LL(b)]}
                options={routeStyle}
                onClick={() =>
                  setActive({
                    kind: "route",
                    from: hubMap[a],
                    to: hubMap[b],
                  })
                }
              />
              {showNauticalMiles && (
                <OverlayView position={midpoint} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div style={distanceLabelStyle}>{nauticalMilesBetween(hubMap[a], hubMap[b])} NM</div>
                </OverlayView>
              )}
              </React.Fragment>
            );
          })}

        {/* STORAGE CIRCLES */}
        {showStorage &&
          hubs
            .filter((h) => h.type === "storage")
            .map((h) => {
              const cfg =
                storageCovers[h.slug] || {
                  center: { lat: h.lat, lng: h.lng },
                  radius: 35000,
                };
              return (
                <Circle
                  key={h.slug}
                  center={cfg.center}
                  radius={cfg.radius}
                  options={{
                    fillColor: "#f3a433",
                    fillOpacity: 0.25,
                    strokeColor: "#f3a433",
                    strokeOpacity: 0.6,
                    strokeWeight: 1,
                  }}
                />
              );
            })}

        <Polyline path={hurricaneBelt} options={dashedRed} geodesic />

        {/* HUB MARKERS */}
        {hubs.map((h) => {
          if (
            (h.type === "berthing" && !showBerthing) ||
            (h.type === "storage" && !showStorage)
          )
            return null;

          return (
            <Marker
              key={h.slug}
              position={{ lat: h.lat, lng: h.lng }}
              onClick={() => setActive({ kind: "hub", ...h })}
              title={h.name}
            />
          );
        })}

        {/* HUB INFO + DIRECTORY LINK */}
        {active?.kind === "hub" && (
          <InfoWindow
            position={{ lat: active.lat, lng: active.lng }}
            onCloseClick={() => setActive(null)}
          >
            <div style={{ maxWidth: 220 }}>
              <strong>{active.name}</strong>
              <strong>{language.startsWith("fr")? (active.name_fr || active.name) : (active.name_en || active.name)}</strong>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                {active.type === "storage"
                  ? "Major yacht storage hub"
                  : "Major berthing/home-port hub"}
              </div>
              <a
                href={`/directory?island=${active.slug}`}
                style={{ color: "#1e67ff", fontWeight: 600, fontSize: 13 }}
              >
                View services →
              </a>
            </div>
          </InfoWindow>
        )}

        {/* ROUTE DISTANCE */}
        {active?.kind === "route" && (
          <InfoWindow
            position={{
              lat: (active.from.lat + active.to.lat) / 2,
              lng: (active.from.lng + active.to.lng) / 2,
            }}
            onCloseClick={() => setActive(null)}
          >
            <div style={{ fontSize: 13 }}>
              <strong>
                {active.from.name} → {active.to.name}
              </strong>
              <div>
                Distance:{" "}
                <strong>
                  {nauticalMilesBetween(active.from, active.to)} NM
                </strong>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* LEGEND  */}
      <div style={legendStyle}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Layers</div>
        <label style={rowStyle}>
          <input
            type="checkbox"
            checked={showRoutes}
            onChange={(e) => setShowRoutes(e.target.checked)}
          />
          <span style={{ marginLeft: 8 }}>Routes</span>
        </label>
        <label style={rowStyle}>
          <input
            type="checkbox"
            checked={showNauticalMiles}
            onChange={(e) => setShowNauticalMiles(e.target.checked)}
          />
          <span style={{ marginLeft: 8 }}>Nautical Miles</span>
        </label>
        <label style={rowStyle}>
          <input
            type="checkbox"
            checked={showBerthing}
            onChange={(e) => setShowBerthing(e.target.checked)}
          />
          <span style={{ marginLeft: 8 }}>Major Berthing Hubs</span>
        </label>
        <label style={rowStyle}>
          <input
            type="checkbox"
            checked={showStorage}
            onChange={(e) => setShowStorage(e.target.checked)}
          />
          <span style={{ marginLeft: 8 }}>Major Storage Hubs</span>
        </label>
      </div>
    </div>
  );
}

const legendStyle = {
  position: "absolute",
  top: 10,
  left: 10,
  background: "rgba(255,255,255,0.95)",
  border: "1px solid #d6d9df",
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 13,
  zIndex: 5,
};

const rowStyle = { display: "flex", alignItems: "center", margin: "4px 0" };

const distanceLabelStyle = {
  transform: "translate(-50%, -50%)",
  background: "rgba(255,255,255,0.94)",
  border: "1px solid #b8c4ce",
  borderRadius: 4,
  padding: "2px 5px",
  color: "#1b4257",
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: "nowrap",
};
